import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import DEFAULT_AVATAR from "../../constants/Avatar";
import { useAuth } from "../../context/AuthContext";
import {
  fetchNoShowByAppointmentId,
  fetchProviderDetails,
} from "../../utils/APIHelper";
import { getContextAwareToken } from "../../utils/storageHelper";
import { NoShowCard } from "../shared/NoShowCard";

interface Patient {
  id: number;
  name: string;
  diagnosis: string;
  reason: string;
  avatar?: string;
  lastAppointment: string;
  time: string;
  contact: string;
  visitType: string;
  status: string;
  appointmentId: string;
  cancelReason?: string;
  visitMode?: string;
  noshow: string;
  email?: string;
  patientId?: string;
  providerId?: string;
  insurance?: string;
  dob?: string;
}

export default function NoShowDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { appointmentId } = useParams();

  const [patient, setPatient] = useState<Patient | null>(
    (location.state as any)?.patient || null
  );
  const [loading, setLoading] = useState(false);
  const [providerName, setProviderName] = useState<string>("");

  // Log patient data for debugging
  useEffect(() => {
    console.log("=== Patient Data Debug ===");
    console.log("Patient data in NoShowDetail:", patient);
    console.log("Patient email:", patient?.email);
    console.log("Patient contact:", patient?.contact);
    console.log(
      "Full patient object keys:",
      patient ? Object.keys(patient) : "null"
    );
    console.log("Patient email value:", JSON.stringify(patient?.email));
    console.log("Patient email length:", patient?.email?.length);
    if (patient?.email === "" || patient?.email === "N/A") {
      console.warn(
        "⚠️ Patient email is empty or N/A - data needs to be refreshed from backend"
      );
    }
  }, [patient]);

  useEffect(() => {
    const fetchNoShowDetail = async () => {
      if (patient && patient.providerId) {
        // Try to fetch provider details
        try {
          const token = getContextAwareToken(user?.token, user?.role) || "";

          console.log("Fetching provider details for ID:", patient.providerId);
          const providerData = await fetchProviderDetails(
            token,
            patient.providerId
          );
          console.log("Provider data received:", providerData);

          if (providerData && providerData.data) {
            const provider = providerData.data;
            if (provider.first_name && provider.last_name) {
              setProviderName(
                `Dr. ${provider.first_name} ${provider.last_name}`
              );
            }
          } else if (
            providerData &&
            providerData.first_name &&
            providerData.last_name
          ) {
            setProviderName(
              `Dr. ${providerData.first_name} ${providerData.last_name}`
            );
          }
        } catch (error) {
          console.error("Error fetching provider details:", error);
        }
      }

      // If no patient in state, try to fetch from API
      if (!patient && appointmentId) {
        setLoading(true);
        try {
          const token = getContextAwareToken(user?.token, user?.role) || "";

          const record = await fetchNoShowByAppointmentId(token, appointmentId);

          if (record) {
            const appointmentDate = new Date(
              record.appointment?.appointment_date
            );
            const formattedDate = `${(appointmentDate.getMonth() + 1)
              .toString()
              .padStart(2, "0")}/${appointmentDate
              .getDate()
              .toString()
              .padStart(2, "0")}/${appointmentDate.getFullYear()}`;

            const firstName = record.patient?.patient?.first_name || "";
            const lastName = record.patient?.patient?.last_name || "";
            const patientName =
              `${firstName} ${lastName}`.trim() || "Unknown Patient";

            // Format DOB
            const dobRaw =
              record.patient?.patient?.birth_date ||
              record.patient?.patient?.date_of_birth;
            const formattedDOB = dobRaw
              ? dayjs(dobRaw).format("MM/DD/YYYY")
              : "N/A";

            const mappedPatient: Patient = {
              id: record.id,
              name: patientName,
              avatar: DEFAULT_AVATAR,
              lastAppointment: formattedDate,
              time:
                record.appointment?.appointment_time?.substring(0, 5) || "N/A",
              contact: record.patient?.patient?.contact_number || "N/A",
              email: record.patient?.email || "N/A", // Email is at record.patient.email
              visitType: record.appointment?.visit_type || "In Person",
              reason: record.appointment?.reason_for_visit || "Not specified",
              diagnosis: record.appointment?.diagnosis || "Not provided",
              status: "Canceled",
              appointmentId: record.appointment_id || "",
              cancelReason: record.reason_cancellation || "No reason provided",
              noshow: record.cancellation_date || "",
              patientId: record.patient_id,
              providerId: record.provider_id,
              dob: formattedDOB,
              insurance: record.appointment?.insurance || "N/A",
            };

            setPatient(mappedPatient);

            // Set provider name if available from the record
            if (
              record.provider &&
              record.provider.first_name &&
              record.provider.last_name
            ) {
              setProviderName(
                `Dr. ${record.provider.first_name} ${record.provider.last_name}`
              );
            } else if (record.provider_id) {
              // Fallback: fetch provider details separately
              try {
                const providerData = await fetchProviderDetails(
                  token,
                  record.provider_id
                );
                console.log("Provider data from fallback fetch:", providerData);

                if (providerData && providerData.data) {
                  const provider = providerData.data;
                  if (provider.first_name && provider.last_name) {
                    setProviderName(
                      `Dr. ${provider.first_name} ${provider.last_name}`
                    );
                  }
                } else if (
                  providerData &&
                  providerData.first_name &&
                  providerData.last_name
                ) {
                  setProviderName(
                    `Dr. ${providerData.first_name} ${providerData.last_name}`
                  );
                }
              } catch (err) {
                console.error("Error fetching provider in fallback:", err);
              }
            }
          } else {
            toast.error("No-show appointment not found");
            navigate("/layout/noshow");
          }
        } catch (error) {
          console.error("Error fetching no-show detail:", error);
          toast.error("Failed to load appointment details");
          navigate("/layout/noshow");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchNoShowDetail();
  }, [appointmentId, patient, user, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!patient) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography>Loading patient details...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ width: "100%", padding: "12px 20px", backgroundColor: "#FAFAFA" }}
    >
      {/* Header Section */}
      <Box sx={{ marginBottom: "16px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "8px",
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <IconButton
                onClick={() => navigate("/layout/noshow")}
                sx={{
                  padding: "0",
                  marginRight: "8px",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: "24px", color: "#2D3748" }} />
              </IconButton>
              <Typography
                sx={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: "#2D3748",
                  fontFamily: "Quicksand, sans-serif",
                }}
              >
                No-Show
              </Typography>
              <Typography
                sx={{
                  fontSize: "28px",
                  fontWeight: 300,
                  color: "#CBD5E0",
                  fontFamily: "Quicksand, sans-serif",
                  margin: "0 4px",
                }}
              >
                /
              </Typography>
              <Typography
                sx={{
                  fontSize: "28px",
                  fontWeight: 400,
                  color: "#A0AEC0",
                  fontFamily: "Quicksand, sans-serif",
                }}
              >
                {patient.name}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#A0AEC0",
                fontWeight: 400,
                fontFamily: "Quicksand, sans-serif",
              }}
            >
              Send empathetic messages to rebook no-shows and cancellations.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <IconButton
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                padding: "10px",
                "&:hover": {
                  borderColor: "#CBD5E0",
                  backgroundColor: "#F7FAFC",
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: "20px", color: "#2D3748" }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Patient Info Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          border: "1px solid #E2E8F0",
          padding: "20px 24px",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Avatar
            src={patient.avatar || DEFAULT_AVATAR}
            alt={patient.name}
            sx={{ width: 64, height: 64 }}
          />
          <Box>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: "#2D3748",
                fontFamily: "Quicksand, sans-serif",
                marginBottom: "6px",
              }}
            >
              {patient.name}
            </Typography>
            <Box sx={{ display: "flex", gap: "24px" }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#A0AEC0",
                  fontFamily: "Quicksand, sans-serif",
                }}
              >
                ID: {patient.id}
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#A0AEC0",
                  fontFamily: "Quicksand, sans-serif",
                }}
              >
                DOB: {patient.dob || "05/12/1985"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#A0AEC0",
                  fontFamily: "Quicksand, sans-serif",
                }}
              >
                Insurance: {patient.insurance || "Blue Shield PPO"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Contact Info Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            padding: "16px 20px",
            border: "1px solid #F7FAFC",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: "#A0AEC0",
              fontFamily: "Quicksand, sans-serif",
              marginBottom: "6px",
            }}
          >
            Phone
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#2D3748",
              fontFamily: "Quicksand, sans-serif",
            }}
          >
            {patient.contact || "N/A"}
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            padding: "16px 20px",
            border: "1px solid #F7FAFC",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: "#A0AEC0",
              fontFamily: "Quicksand, sans-serif",
              marginBottom: "6px",
            }}
          >
            Email
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#2D3748",
              fontFamily: "Quicksand, sans-serif",
            }}
          >
            {patient.email || "N/A"}
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            padding: "16px 20px",
            border: "1px solid #F7FAFC",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: "#A0AEC0",
              fontFamily: "Quicksand, sans-serif",
              marginBottom: "6px",
            }}
          >
            Primary Care
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#2D3748",
              fontFamily: "Quicksand, sans-serif",
            }}
          >
            {providerName}
          </Typography>
        </Box>
      </Box>

      {/* No Show Reduction Activity Section */}
      <Box sx={{ marginBottom: "24px" }}>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#2D3748",
            marginBottom: "16px",
            fontFamily: "Quicksand, sans-serif",
          }}
        >
          No Show Reduction Activity
        </Typography>

        <NoShowCard
          noshow={patient.noshow}
          date={patient.lastAppointment}
          time={patient.time}
          title={patient.reason || "Consultation"}
          doctor={providerName}
          location="1629 North 45th Street"
          duration="30 Minutes"
          status={patient.status}
          reason={patient.cancelReason || "cancel for no reason"}
          onViewDetails={() => {
            if (patient.appointmentId) {
              console.log(
                "Navigating to:",
                `/layout/noshow/activity/${patient.appointmentId}`
              );
              navigate(`/layout/noshow/activity/${patient.appointmentId}`, {
                state: { patient },
              });
            } else {
              console.warn("Missing appointment ID");
            }
          }}
          onReschedule={() =>
            alert(`Rescheduling appointment for ${patient.name}`)
          }
        />
      </Box>
    </Box>
  );
}
